import {
  Controller,
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import { useFormProps } from "./page-props"
import Anchor from "@/components/ui/anchor"
import { FormField, FormGroup } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import {
  EducatorsListingProfilesTeamListingProfileForm,
  EducatorsListingProfilesTag,
} from "@/types/serializers"
import { Globe, Lock, User, Users } from "lucide-react"
import { Toggle } from "@/components/ui/toggle"

export default function ListingProfileForm({
  formId,
  titleId,
  onSubmit,
}: {
  onSubmit: (data: {
    listing_profile: EducatorsListingProfilesTeamListingProfileForm
  }) => void
  formId: string
  titleId: string
}) {
  const { listing_profile, tag_categories } = useFormProps()

  const { t } = useTranslation(["listing_profiles"])
  const methods = useForm<{
    listing_profile: EducatorsListingProfilesTeamListingProfileForm
  }>({
    defaultValues: { listing_profile },
  })
  return (
    <form
      role="form"
      id={formId}
      onSubmit={methods.handleSubmit(onSubmit)}
      className="relative flex min-h-0 flex-1 flex-col gap-5"
      aria-labelledby={titleId}
      data-testid="listing-profile-form"
    >
      <FormProvider {...methods}>
        <RHFDirtyDraftRegistration />
        <Anchor>
          <Card className="absolute h-full w-full">
            <Anchor.ScrollingVisibilityRoot className="flex h-full flex-row gap-6">
              <nav
                className="border-l-brand-dark-grey sticky top-0 flex w-min shrink-0 flex-col items-start overflow-y-scroll border-r-2 px-2"
                aria-label={t(
                  "listing_profiles:form.sections.navigation_title",
                )}
              >
                <Anchor.HashLink
                  sectionKey="listing_details"
                  title={t("listing_profiles:form.sections.listing_details")}
                />
                <Anchor.HashLink
                  sectionKey="listing_tags"
                  title={t("listing_profiles:form.sections.listing_tags")}
                  id="listing_tags"
                />
                <Anchor.HashItemProvider sectionKey="listing_tags">
                  <ul
                    aria-labelledby="listing_tags"
                    className="flex flex-col flex-wrap items-start gap-1"
                  >
                    {tag_categories.map((category) => (
                      <Anchor.ScrollingLink
                        key={category.id}
                        sectionKey={category.id}
                        title={category.name}
                      />
                    ))}
                  </ul>
                </Anchor.HashItemProvider>
              </nav>
              <div className="grow overflow-hidden overflow-y-auto scroll-smooth">
                <ListingDetailsSection />
                <ListingTagsSection />
              </div>
            </Anchor.ScrollingVisibilityRoot>
          </Card>
        </Anchor>
      </FormProvider>
    </form>
  )
}

export function ListingDetailsSection() {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<{
    listing_profile: EducatorsListingProfilesTeamListingProfileForm
  }>()

  const { t } = useTranslation(["listing_profiles", "common"])

  return (
    <Anchor.HashSection sectionKey="listing_details">
      <CardHeader>
        <CardTitle id="listing_details_section_title">
          <Anchor.HashTitle />
        </CardTitle>
      </CardHeader>
      <CardContent
        className="py-6"
        role="region"
        aria-labelledby="listing_details_section_title"
      >
        <FormField
          {...register("listing_profile.name", {
            required: t("common:errors.required"),
          })}
          className="space-y-2"
          label={t("listing_profiles:form.listing_details_section.name")}
          errors={errors}
        >
          <Input />
        </FormField>
        <FormGroup
          label={t(
            "listing_profiles:form.listing_details_section.visibility.title",
          )}
          labelId="visibility-label"
          htmlFor="visibility"
        >
          <Controller
            control={control}
            name="listing_profile.visible"
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name="visible"
                required
                aria-labelledby="visibility-label"
                id="visibility"
                defaultValue={value ? "true" : "false"}
                onValueChange={(value) => onChange(value === "true")}
                className="grid w-full grid-cols-2 gap-2"
              >
                <div>
                  <RadioGroupItem
                    value="false"
                    id="private"
                    className="peer sr-only"
                    aria-labelledby="private-label"
                    aria-describedby="private-description"
                  />
                  <Label
                    htmlFor="private"
                    className="border-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary peer-data-[state=checked]:bg-primary/10 peer-data-[state=checked]:hover:bg-primary/15 peer-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex h-full w-full cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors"
                  >
                    <Lock
                      className="mt-0.5 size-6 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium" id="private-label">
                        {t(
                          "listing_profiles:form.listing_details_section.visibility.private",
                        )}
                      </span>
                      <span className="text-xs" id="private-description">
                        {t(
                          "listing_profiles:form.listing_details_section.visibility.private_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>

                <div>
                  <RadioGroupItem
                    value="true"
                    id="public"
                    className="peer sr-only"
                    aria-labelledby="public-label"
                    aria-describedby="public-description"
                  />
                  <Label
                    htmlFor="public"
                    className="border-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:hover:bg-primary/15 peer-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex h-full w-full cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors"
                  >
                    <Globe
                      className="mt-0.5 size-6 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium" id="public-label">
                        {t(
                          "listing_profiles:form.listing_details_section.visibility.public",
                        )}
                      </span>
                      <span className="text-xs" id="public-description">
                        {t(
                          "listing_profiles:form.listing_details_section.visibility.public_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
        </FormGroup>
        <FormGroup
          label={t(
            "listing_profiles:form.listing_details_section.classes_listing_type.title",
          )}
          labelId="classes-listing-type-label"
          htmlFor="classes-listing-type"
        >
          <Controller
            control={control}
            name="listing_profile.classes_listing_type"
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name="visible"
                required
                aria-labelledby="classes-listing-type-label"
                id="classes-listing-type"
                defaultValue={value || "separate"}
                onValueChange={(value) => onChange(value)}
                className="grid w-full grid-cols-2 gap-2"
              >
                <div>
                  <RadioGroupItem
                    value="separate"
                    id="separate"
                    className="peer sr-only"
                    aria-labelledby="separate-label"
                    aria-describedby="separate-description"
                  />
                  <Label
                    htmlFor="separate"
                    className="border-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary peer-data-[state=checked]:bg-primary/10 peer-data-[state=checked]:hover:bg-primary/15 peer-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex h-full w-full cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors"
                  >
                    <User
                      className="mt-0.5 size-6 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium" id="separate-label">
                        {t(
                          "listing_profiles:form.listing_details_section.classes_listing_type.separate",
                        )}
                      </span>
                      <span className="text-xs" id="separate-description">
                        {t(
                          "listing_profiles:form.listing_details_section.classes_listing_type.separate_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>

                <div>
                  <RadioGroupItem
                    value="shared"
                    id="shared"
                    className="peer sr-only"
                    aria-labelledby="shared-label"
                    aria-describedby="shared-description"
                  />
                  <Label
                    htmlFor="shared"
                    className="border-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:hover:bg-primary/15 peer-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex h-full w-full cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors"
                  >
                    <Users
                      className="mt-0.5 size-6 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium" id="shared-label">
                        {t(
                          "listing_profiles:form.listing_details_section.classes_listing_type.shared",
                        )}
                      </span>
                      <span className="text-xs" id="shared-description">
                        {t(
                          "listing_profiles:form.listing_details_section.classes_listing_type.shared_description",
                        )}
                      </span>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
        </FormGroup>
        <FormField
          {...register("listing_profile.webpage_url")}
          className="space-y-2"
          label={t("listing_profiles:form.listing_details_section.webpage_url")}
        >
          <Input type="url" />
        </FormField>
        <FormField
          {...register("listing_profile.video_url")}
          className="space-y-2"
          label={t("listing_profiles:form.listing_details_section.video_url")}
        >
          <Input type="url" />
        </FormField>
        <FormField
          {...register("listing_profile.written_comment")}
          className="space-y-2"
          label={t(
            "listing_profiles:form.listing_details_section.written_comment",
          )}
        >
          <Textarea />
        </FormField>
      </CardContent>
    </Anchor.HashSection>
  )
}

function TagToggle({ tag }: { tag: EducatorsListingProfilesTag }) {
  const { control } = useFormContext<{
    listing_profile: EducatorsListingProfilesTeamListingProfileForm
  }>()
  const {
    field: { value, onChange },
  } = useController({ name: "listing_profile.tag_ids", control })
  return (
    <Toggle
      variant="outline"
      key={tag.id}
      pressed={value.includes(tag.id)}
      className="min-h-fit p-3 wrap-break-word whitespace-normal"
      onPressedChange={(pressed) => {
        if (pressed) {
          onChange([...value, tag.id])
        } else {
          onChange(value.filter((id) => id !== tag.id))
        }
      }}
    >
      {tag.name}
    </Toggle>
  )
}

export function ListingTagsSection() {
  const { t } = useTranslation(["listing_profiles"])
  const { tag_categories } = useFormProps()
  return (
    <Anchor.HashSection sectionKey="listing_tags">
      <CardHeader>
        <CardTitle>
          <Anchor.HashTitle />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {t("listing_profiles:form.listing_tags_section.description")}
      </CardContent>
      {tag_categories.map(({ id, description, tag_subcategories }) => (
        <Anchor.ScrollingSection sectionKey={id} key={id}>
          <CardHeader>
            <CardTitle className="text-xl">
              <Anchor.ScrollingTitle />
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6" role="region" aria-labelledby={id}>
            <div className="flex flex-col gap-6" key={id}>
              <p>{description}</p>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {tag_subcategories.map((subcategory) => (
                  <Card key={subcategory.id}>
                    <CardHeader>
                      <CardTitle
                        className="text-lg"
                        id={subcategory.id}
                        data-testid="listing-tag-subcategory-title"
                      >
                        {subcategory.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul
                        className="flex flex-row flex-wrap gap-2"
                        aria-labelledby={subcategory.id}
                      >
                        {subcategory.tags.map((tag) => (
                          <TagToggle tag={tag} key={tag.id} />
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </CardContent>
        </Anchor.ScrollingSection>
      ))}
    </Anchor.HashSection>
  )
}
